/**
 * Rascunho do editor de um guia. Lembra a versão (e o conteúdo) em que se baseia, e é essa
 * que vai ao guardar: se o guia mudar noutro sítio, guardar dá conflito em vez de apagar as
 * alterações dos outros (antes, o guia recarregado da conta, por exemplo ao voltar à janela,
 * passava a ser a base e guardar escrevia por cima). Fica também no sessionStorage do
 * separador: recarregar a página não perde as alterações por guardar.
 */
import type { Locale, RemoteGuide, Step } from '@nolens/domain';
import { useEffect, useState } from 'react';

export type Draft = { title: string; description: string; language: Locale; steps: Step[] };

/** A versão do guia em que o rascunho se baseia e o conteúdo dela. */
export interface Base {
  version: number;
  content: Draft;
}

export const toDraft = (
  g: Pick<RemoteGuide, 'title' | 'description' | 'language' | 'steps'>,
): Draft => ({
  title: g.title,
  description: g.description,
  language: g.language,
  steps: g.steps,
});

export const sameDraft = (a: Draft, b: Draft) =>
  a.title === b.title &&
  a.description === b.description &&
  a.language === b.language &&
  JSON.stringify(a.steps) === JSON.stringify(b.steps);

/**
 * O guia da conta tem outra versão: o que fazer ao rascunho.
 *  - 'version': só mudou a versão (partilhar, mudar de espaço), o conteúdo é o mesmo;
 *  - 'adopt': sem alterações aqui (ou já iguais às da conta): passa à versão nova;
 *  - 'conflict': alterado noutro sítio e aqui também.
 */
export function syncOf(
  latest: RemoteGuide,
  base: Base,
  draft: Draft,
): 'same' | 'version' | 'adopt' | 'conflict' {
  if (latest.version === base.version) return 'same';
  const content = toDraft(latest);
  if (sameDraft(content, base.content)) return 'version';
  if (sameDraft(draft, base.content) || sameDraft(draft, content)) return 'adopt';
  return 'conflict';
}

interface Stored {
  base: Base;
  draft: Draft;
}

const key = (guideId: string) => 'nolens-draft:' + guideId;

export function readStored(guideId: string): Stored | null {
  try {
    const raw = sessionStorage.getItem(key(guideId));
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

export function writeStored(guideId: string, s: Stored | null): void {
  try {
    if (s) sessionStorage.setItem(key(guideId), JSON.stringify(s));
    else sessionStorage.removeItem(key(guideId));
  } catch {
    // armazenamento cheio ou indisponível: o rascunho fica só na página
  }
}

export function useGuideDraft(guide: RemoteGuide) {
  const [stored] = useState(() => readStored(guide.id));
  const [base, setBase] = useState<Base>(
    () => stored?.base ?? { version: guide.version, content: toDraft(guide) },
  );
  const [draft, setDraft] = useState<Draft>(() => stored?.draft ?? toDraft(guide));
  const [restored, setRestored] = useState(!!stored);
  const dirty = !sameDraft(draft, base.content);
  const sync = syncOf(guide, base, draft);

  // O guia da conta mudou: acerta já, ainda nesta passagem (o React volta a desenhar antes de
  // mostrar; na seguinte, a versão já é a mesma)
  if (sync === 'version') setBase({ ...base, version: guide.version });
  if (sync === 'adopt') {
    setBase({ version: guide.version, content: toDraft(guide) });
    setDraft(toDraft(guide));
  }

  useEffect(() => {
    const t = setTimeout(() => writeStored(guide.id, dirty ? { base, draft } : null), 300);
    return () => clearTimeout(t);
  }, [guide.id, dirty, base, draft]);

  return {
    draft,
    setDraft,
    base,
    dirty,
    /** Alterado noutro sítio, com alterações aqui por guardar. */
    stale: sync === 'conflict',
    /** Alterações por guardar recuperadas do separador (a página foi recarregada). */
    restored: restored && dirty,
    /** A conta tem esta versão (guardada aqui, ou uma captura alterada aqui): passa a ser a base. */
    rebase(g: RemoteGuide, next: Draft = toDraft(g)) {
      setBase({ version: g.version, content: toDraft(g) });
      setDraft(next);
      setRestored(false);
    },
    /** Sai sem guardar: o rascunho deixa de ficar no separador. */
    forget() {
      writeStored(guide.id, null);
    },
  };
}
